<template>
  <div class="wrapper">
    <admin-title :title="$route.meta.name" />

    <el-tabs v-model="activeTab" @tab-click="onTabChange">
      <el-tab-pane label="3C商品模板" name="1" />
      <el-tab-pane label="电车商品模板" name="2" />
    </el-tabs>

    <div class="page-toolbar">
      <el-button
        v-checkbtn="isShowBtn(AUTH_BTN.store_goods_price_tpl_create)"
        type="primary"
        size="small"
        icon="el-icon-plus"
        @click="handleCreate"
      >
        新增模板
      </el-button>
    </div>

    <el-row :gutter="16" class="card-list" v-loading="tableLoading">
      <el-col
        v-for="item in pageList"
        :key="item.id"
        :span="6"
        class="card-list-item"
      >
        <el-card shadow="hover" class="template-card">
          <div class="card-head">
            <span class="card-title">{{ item.templateName }}</span>
            <i
              v-checkbtn="isShowBtn(AUTH_BTN.store_goods_price_tpl_rename)"
              class="el-icon-edit-outline action-icon"
              title="修改模板名称"
              @click="handleRename(item)"
            />
          </div>

          <div class="card-tags">
            <el-tag
              v-if="item.isDefault"
              size="small"
              type="primary"
            >默认模板</el-tag>
            <el-tag size="small" type="warning">{{ goodsTypeLabel }}</el-tag>
          </div>

          <div class="card-time">创建时间：{{ item.createTime || '-' }}</div>
          <el-divider></el-divider>

          <div class="card-actions">
            <el-link
              v-checkbtn="isShowBtn(AUTH_BTN.store_goods_price_tpl_detail)"
              :underline="false"
              type="primary"
              @click="handleDetail(item)"
            >
              查看详情
            </el-link>
            <el-link
              v-checkbtn="isShowBtn(AUTH_BTN.store_goods_price_tpl_export)"
              :underline="false"
              type="primary"
              @click="handleExport(item)"
            >
              导出
            </el-link>
            <el-link
              v-checkbtn="isShowBtn(AUTH_BTN.store_goods_price_tpl_import)"
              :underline="false"
              type="primary"
              @click="handleImport(item)"
            >
              数据导入
            </el-link>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <div v-if="!tableLoading && pageList.length === 0" class="empty-tip">
      暂无数据
    </div>

    <div class="pagination-wrapper">
      <el-pagination
        background
        layout="prev, pager, next"
        :current-page.sync="table.currentPage"
        :page-size="table.pageSize"
        :total="table.total"
      />
    </div>

    <CreateRenameDialog
      :visible.sync="templateDialog.visible"
      :mode="templateDialog.mode"
      :editId="templateDialog.editId"
      :initialName="templateDialog.initialName"
      @submit="onSubmitTemplate"
    />

    <ImportDialog
      :visible.sync="importDialog.visible"
      :tpl-id="importDialog.tplId"
      @submit="onImportSubmit"
    />
  </div>
</template>

<script>
import { authBtnMixin } from "@/mixins/authBtnMixin";
import CreateRenameDialog from "./components/create-rename-dialog.vue";
import ImportDialog from "./components/import-dialog.vue";
import { getTemplatePage, addTemplate, editTemplate, exportTemplate, importTemplate } from "./api.js";

export default {
  name: "StoreGoodsPriceTemplate",
  components: { CreateRenameDialog, ImportDialog },
  mixins: [authBtnMixin],
  data() {
    return {
      activeTab: "1",
      table: {
        currentPage: 1,
        pageSize: 12,
        total: 0,
      },
      tableLoading: false,
      pageList: [],
      templateDialog: {
        visible: false,
        mode: "create",
        editId: null,
        initialName: "",
      },
      importDialog: {
        visible: false,
        tplId: null,
      },
    };
  },
  provide() {
    return {
      context: this,
    };
  },
  computed: {
    goodsTypeLabel() {
      return this.activeTab === "1" ? "3C商品模板" : "电车商品模板";
    },
  },
  watch: {
    "table.currentPage"() {
      this.getList();
    },
    "table.pageSize"() {
      this.getList();
    },
  },
  mounted() {
    this.getList();
  },
  methods: {
    // 获取模板列表
    async getList() {
      this.tableLoading = true;
      try {
        const res = await getTemplatePage({
          goodsType: Number(this.activeTab),
          page: this.table.currentPage,
          pageSize: this.table.pageSize,
        });
        this.pageList = (res.list || []).map((item) => ({
          id: item.id,
          templateName: item.tplName,
          isDefault: item.isDefault,
          status: item.status,
          createTime: item.createTime || "",
        }));
        this.table.total = res.total || 0;
      } finally {
        this.tableLoading = false;
      }
    },
    applySearch(resetPage = false) {
      if (resetPage && this.table.currentPage !== 1) {
        this.table.currentPage = 1;
        return;
      }
      this.getList();
    },
    // 切换 tab
    onTabChange() {
      this.applySearch(true);
    },
    // 新增模板
    handleCreate() {
      this.templateDialog = {
        visible: true,
        mode: "create",
        editId: null,
        initialName: "",
      };
    },
    // 重命名模板
    handleRename(item) {
      this.templateDialog = {
        visible: true,
        mode: "rename",
        editId: item.id,
        initialName: item.templateName,
      };
    },
    async onSubmitTemplate({ mode, id, name, done, fail }) {
      try {
        if (mode === "create") {
          await addTemplate({
            tplName: name,
            goodsType: Number(this.activeTab),
          });
          this.$message.success("新增成功");
        } else {
          await editTemplate({
            id: String(id),
            tplName: name,
          });
          this.$message.success("修改成功");
        }
        done();
        this.applySearch(true);
      } catch {
        fail();
      }
    },
    handleDetail(item) {
      this.$router.push({
        name: "StoreGoodsPriceTemplateDetail",
        query: {
          tplId: item.id,
          goodsType: this.activeTab,
          name: item.templateName,
        },
      });
    },
    handleExport(item) {
      exportTemplate({ tplId: String(item.id) }, item.templateName);
    },
    handleImport(item) {
      this.importDialog.tplId = item.id;
      this.importDialog.visible = true;
    },
    async onImportSubmit({ file, tplId, done, fail }) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("tplId", String(tplId));
        await importTemplate(formData);
        this.$message.success("导入成功");
        done();
        this.applySearch(true);
      } catch {
        fail();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.page-toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
}

.card-list {
  display: flex;
  flex-wrap: wrap;
}

.card-list-item {
  margin-bottom: 16px;
  display: flex;

  /deep/ .el-card {
    width: 100%;
  }
}

.template-card {
  display: flex;
  flex-direction: column;

  /deep/ .el-card__body {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .card-actions {
    margin-top: auto;
  }

  .card-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 24px;
    margin-bottom: 8px;

    .card-title {
      font-size: 15px;
      font-weight: 600;
      color: #303133;
    }

    .action-icon {
      cursor: pointer;
      color: #409eff;
      font-size: 16px;
      flex-shrink: 0;
    }
  }

  .card-tags {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
  }

  .card-time {
    font-size: 12px;
    color: #909399;
    margin-bottom: 0;
  }

  /deep/ .el-divider--horizontal {
    margin: 10px 0;
  }

  .card-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }
}

.empty-tip {
  text-align: center;
  color: #909399;
  padding: 60px 0;
  font-size: 14px;
}

.pagination-wrapper {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}

</style>
